import { Suspense, useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import type { Clinica, SituacaoPaciente } from '@/lib/types'
import { listClinicas } from '@/services/clinicas'
import { criarPaciente, getPaciente, atualizarPaciente } from '@/services/pacientes'

function FormPaciente() {
  const { current } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const editId = params.get('id')

  const [clinicas, setClinicas] = useState<Clinica[]>([])
  const [nome, setNome] = useState('')
  const [cpf, setCpf] = useState('')
  const [dataNasc, setDataNasc] = useState('')
  const [genero, setGenero] = useState('')
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [proximaAcao, setProximaAcao] = useState('')
  const [situacao, setSituacao] = useState<SituacaoPaciente>('ativa')
  const [clinicaIds, setClinicaIds] = useState<string[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  const carregar = useCallback(async () => {
    if (!current) return
    try {
      const cs = await listClinicas(current.workspace.id)
      setClinicas(cs.filter(c => c.ativo))
      if (editId) {
        const p = await getPaciente(current.workspace.id, editId)
        if (p) {
          setNome(p.nome); setCpf(p.cpf ?? ''); setDataNasc(p.data_nasc ?? '')
          setGenero(p.genero ?? ''); setEmail(p.email ?? ''); setTelefone(p.telefone ?? '')
          setObservacoes(p.observacoes ?? ''); setProximaAcao(p.proxima_acao ?? '')
          setSituacao(p.situacao); setClinicaIds(p.clinica_ids)
        }
      }
    } finally {
      setLoading(false)
    }
  }, [current, editId])

  useEffect(() => { carregar() }, [carregar])

  function toggleClinica(id: string) {
    setClinicaIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    if (!current) return
    if (nome.trim().length < 3) { setErro('Informe o nome completo.'); return }
    setBusy(true)
    setErro(null)
    const dados = {
      nome: nome.trim(),
      cpf: cpf || null,
      data_nasc: dataNasc || null,
      genero: genero || null,
      email: email || null,
      telefone: telefone || null,
      observacoes: observacoes || null,
      proxima_acao: proximaAcao || null,
      situacao,
    }
    try {
      if (editId) {
        await atualizarPaciente({ workspaceId: current.workspace.id, id: editId, dados, clinicaIds })
        navigate(`/pacientes/${editId}`)
      } else {
        const id = await criarPaciente({ workspaceId: current.workspace.id, dados, clinicaIds })
        navigate(`/pacientes/${id}`)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      setErro(msg.includes('uq_paciente_cpf') ? 'Já existe um paciente com esse CPF.' : 'Não foi possível salvar o paciente.')
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    )
  }

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: 16 }}>{editId ? 'Editar paciente' : 'Novo paciente'}</h1>

      <form onSubmit={salvar} className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label className="field-label" htmlFor="p-nome">Nome completo *</label>
          <input id="p-nome" required className="field-input" value={nome} onChange={e => setNome(e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label className="field-label" htmlFor="p-cpf">CPF</label>
            <input id="p-cpf" className="field-input" inputMode="numeric" value={cpf} onChange={e => setCpf(e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="p-nasc">Data de nascimento</label>
            <input id="p-nasc" type="date" className="field-input" value={dataNasc} onChange={e => setDataNasc(e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="p-genero">Gênero</label>
            <select id="p-genero" className="field-input" value={genero} onChange={e => setGenero(e.target.value)}>
              <option value="">Não informar</option>
              <option value="feminino">Feminino</option>
              <option value="masculino">Masculino</option>
              <option value="outro">Outro</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <label className="field-label" htmlFor="p-tel">Telefone / WhatsApp</label>
            <input id="p-tel" className="field-input" inputMode="tel" value={telefone} onChange={e => setTelefone(e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="p-email">E-mail</label>
            <input id="p-email" type="email" className="field-input" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
        </div>

        <div>
          <span className="field-label">Clínicas onde é atendida</span>
          {clinicas.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              Nenhuma clínica cadastrada ainda — você pode cadastrar em Clínicas.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {clinicas.map(c => {
                const on = clinicaIds.includes(c.id)
                return (
                  <button
                    key={c.id} type="button" onClick={() => toggleClinica(c.id)}
                    aria-pressed={on}
                    className="badge"
                    style={{
                      cursor: 'pointer', border: '1px solid', fontSize: 13, padding: '7px 12px',
                      background: on ? 'var(--primary-soft)' : 'var(--card)',
                      color: on ? 'var(--primary-deep)' : 'var(--text-secondary)',
                      borderColor: on ? 'var(--primary)' : 'var(--border-input)',
                    }}
                  >
                    {c.nome}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div>
          <label className="field-label" htmlFor="p-prox">Próxima ação</label>
          <input
            id="p-prox" className="field-input" value={proximaAcao} onChange={e => setProximaAcao(e.target.value)}
            placeholder="Ex.: aguardar resultado da mamografia"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="p-obs">Observações</label>
          <textarea id="p-obs" className="field-input" rows={3} style={{ resize: 'vertical' }} value={observacoes} onChange={e => setObservacoes(e.target.value)} />
        </div>
        {editId && (
          <div>
            <label className="field-label" htmlFor="p-sit">Situação</label>
            <select id="p-sit" className="field-input" style={{ maxWidth: 220 }} value={situacao} onChange={e => setSituacao(e.target.value as SituacaoPaciente)}>
              <option value="ativa">Ativa</option>
              <option value="inativa">Inativa</option>
            </select>
          </div>
        )}

        {erro && <div className="field-error" role="alert">{erro}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
            Salvar
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>Cancelar</button>
        </div>
      </form>
    </div>
  )
}

export default function NovoPacientePage() {
  // useSearchParams exige Suspense no build de produção
  return (
    <Suspense fallback={null}>
      <FormPaciente />
    </Suspense>
  )
}
